/**
 * Stripe Service
 * ==============
 * Handles Stripe checkout and subscription management.
 */

import { supabase } from '../lib/supabase'
import { EDGE_FUNCTIONS_CONFIG } from '../config/edge-functions.config'
import { STRIPE_CONFIG, type TierId } from '../config/stripe.config'

export interface CheckoutSessionResult {
  url: string
  sessionId: string
}

export const stripeService = {
  /**
   * Create a Stripe checkout session for a subscription
   */
  async createCheckoutSession(
    tierId: TierId,
    userId: string
  ): Promise<CheckoutSessionResult | null> {
    // Get price ID from config
    const priceId = STRIPE_CONFIG.getPriceId(tierId)

    if (!priceId) {
      console.error(`No price ID configured for tier: ${tierId}`)
      console.error('Please set the Stripe price IDs in your .env file:')
      console.error('  VITE_STRIPE_PRICE_BASIC=price_xxx')
      console.error('  VITE_STRIPE_PRICE_ADVANCED=price_xxx')
      console.error('  VITE_STRIPE_PRICE_CORPORATE=price_xxx')
      return null
    }

    try {
      const response = await fetch(
        `${EDGE_FUNCTIONS_CONFIG.getEdgeFunctionsUrl()}/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            priceId,
            userId,
            successUrl: `${window.location.origin}/dashboard?subscription=success`,
            cancelUrl: `${window.location.origin}/dashboard/subscription?cancelled=true`,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Checkout session error:', errorData)
        throw new Error(errorData.error || 'Failed to create checkout session')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      return null
    }
  },

  /**
   * Open Stripe customer portal for managing subscription
   */
  async openCustomerPortal(customerId: string): Promise<string | null> {
    try {
      const response = await fetch(
        `${EDGE_FUNCTIONS_CONFIG.getEdgeFunctionsUrl()}/create-portal-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            customerId,
            returnUrl: `${window.location.origin}/dashboard/subscription`,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Portal session error:', errorData)
        throw new Error(errorData.error || 'Failed to create portal session')
      }

      const { url } = await response.json()
      return url
    } catch (error) {
      console.error('Failed to open customer portal:', error)
      return null
    }
  },

  /**
   * Redirect to Stripe checkout
   */
  async redirectToCheckout(tierId: TierId, userId: string): Promise<void> {
    const result = await this.createCheckoutSession(tierId, userId)
    if (result?.url) {
      window.location.href = result.url
    }
  },

  /**
   * Get pricing display for a tier
   */
  getPriceDisplay(tierId: TierId): string {
    const tier = STRIPE_CONFIG.getTier(tierId)
    if (!tier) return 'Unknown'
    if (tier.price === null) {
      return 'Contact Us'
    }
    if (tier.price === 0) {
      return 'Free'
    }
    return `$${tier.price}/mo`
  },

  /**
   * Compare tiers - returns true if newTier is an upgrade from currentTier
   */
  isUpgrade(currentTier: TierId, newTier: TierId): boolean {
    const tierOrder: TierId[] = ['free', 'basic', 'advanced', 'corporate', 'platinum']
    const currentIndex = tierOrder.indexOf(currentTier.toLowerCase() as TierId)
    const newIndex = tierOrder.indexOf(newTier.toLowerCase() as TierId)
    return newIndex > currentIndex
  },

  /**
   * Check if Stripe is properly configured
   */
  isConfigured(): boolean {
    return STRIPE_CONFIG.isConfigured()
  },

  /**
   * Check if price IDs are set up
   */
  hasPriceIds(): boolean {
    return STRIPE_CONFIG.hasPriceIds()
  },

  /**
   * Get the current Stripe mode (test or live)
   */
  isTestMode(): boolean {
    return STRIPE_CONFIG.isTestMode()
  },
}
